/**
 * Doctors and patients. The request of 6 October 2026 (second), items 4 and 5.
 *
 * A case used to carry two names and nothing behind them, so the lab could not
 * ask for every case of one doctor or of one patient without trusting that the
 * name was typed the same way each time. Each is a record now, with its own
 * profile and its cases, and a case is linked to both. The two names stay on
 * the case as well, as the copy every list, search and report reads.
 *
 * A patient is one record whichever doctor they are seen by. The request of
 * 6 October 2026 (third), item 3: the same person at two clinics is one
 * patient, and the profile says they have been to two doctors. Which doctors
 * those are is read off the patient's cases and kept nowhere else. A second
 * record for a different person of the same name is allowed, and is never
 * made by accident: somebody has to ask for it.
 *
 * Each record has an ID made from its number, `DR-0012` and `PT-00031`, never
 * typed and never used twice (item 2 of the same request), and two doctors
 * whose names differ only in a title or in punctuation can be merged into one
 * (item 1).
 *
 * A doctor's telephone number and address are kept in an international form
 * since the request of 6 October 2026 (fifth), section 2: the number as E.164,
 * the address as the parts its country's post asks for (`geo/address.ts`).
 * What was typed before, `mobile` and `deliveryAddress`, is kept beside them
 * as it was until somebody has looked at it.
 *
 * The rules here are the server's, word for word (its ClientRules and
 * ClientNames): the mock server answers with them, the forms check with them
 * before they send, and the tests name them.
 */

import type { AddressInput, DoctorAddress } from '../geo/address';

/** A doctor's record, as the API serves it: nineteen keys, in this order. */
export interface Doctor {
  id: string;
  /** The doctor's ID, `DR-0012`: `doctorCode(id)`, sent so no screen has to make it. */
  code: string;
  name: string;
  clinic: string | null;
  /** The telephone number as E.164, `+971501234567`, or null. */
  phone: string | null;
  /** The same number as people read one, `+971 50 123 4567`, or null. */
  phoneDisplay: string | null;
  /**
   * The number as it was typed before numbers were kept as E.164, exactly as
   * stored, or null. Never written any more: a saved `phone` clears it.
   */
  mobile: string | null;
  email: string | null;
  website: string | null;
  /** The address in its parts, with its label, or null. See `DoctorAddress`. */
  address: DoctorAddress | null;
  /**
   * The address as it was typed before addresses had parts, exactly as stored,
   * or null. Never written any more: a saved `address` clears it.
   */
  deliveryAddress: string | null;
  /** True once the conversion could not read the old `mobile` with certainty. */
  phoneNeedsReview: boolean;
  /** True once the conversion could not place the old `deliveryAddress` with certainty. */
  addressNeedsReview: boolean;
  /** Job orders of this doctor that are not deleted. */
  caseCount: number;
  /** Those of them that are also not dispatched and not archived. */
  activeCaseCount: number;
  /** How many different patients the doctor's cases, the deleted left out, are for. */
  patientCount: number;
  /** When the newest of the doctor's cases was taken in, or null with none. */
  lastCaseAt: string | null;
  createdAt: string;
  rowVersion: string;
}

/**
 * A doctor a patient has been to, as the patient's record carries one: enough
 * to name the doctor and link to the profile, and how many of the patient's
 * cases, the deleted left out, are with that doctor.
 */
export interface PatientDoctor {
  id: string;
  /** The doctor's ID, `DR-0012`. */
  code: string;
  name: string;
  clinic: string | null;
  caseCount: number;
}

/** A patient's record, as the API serves it, with the doctors the patient has been to. */
export interface Patient {
  id: string;
  /** The patient's ID, `PT-00031`: `patientCode(id)`. */
  code: string;
  name: string;
  /**
   * Every doctor the patient has a case with, all of them, the most cases
   * first and by name where two have as many. Empty for a patient added by
   * hand with no case yet.
   */
  doctors: PatientDoctor[];
  /** How many doctors that is: the length of `doctors`. */
  doctorCount: number;
  /** Job orders of this patient that are not deleted. */
  caseCount: number;
  /** Those of them that are also not dispatched and not archived. */
  activeCaseCount: number;
  /** When the newest of the patient's cases was taken in, or null with none. */
  lastCaseAt: string | null;
  createdAt: string;
  rowVersion: string;
}

/**
 * The longest each field may be, as the columns are, measured as the record
 * keeps it. The two names are as long as the names a job order carries.
 */
const NAME_MAX = 160;
export const DOCTOR_NAME_MAX = NAME_MAX;
export const PATIENT_NAME_MAX = NAME_MAX;
export const DOCTOR_CLINIC_MAX = 160;
/** A telephone number as it is sent, before it is read: longer than this is no number. */
export const DOCTOR_PHONE_MAX = 40;
export const DOCTOR_EMAIL_MAX = 160;
export const DOCTOR_WEBSITE_MAX = 200;

/**
 * The five fields of a doctor's record that are one line of text, in the
 * order the form shows them and the server checks them. The address comes
 * after them, part by part: `ADDRESS_PROBLEM_FIELDS`.
 */
export const DOCTOR_FIELDS = ['name', 'clinic', 'phone', 'email', 'website'] as const;
export type DoctorField = (typeof DOCTOR_FIELDS)[number];

/** The same limits, by field, for a form that draws one input per field. */
export const DOCTOR_FIELD_MAX: Record<DoctorField, number> = {
  name: DOCTOR_NAME_MAX,
  clinic: DOCTOR_CLINIC_MAX,
  phone: DOCTOR_PHONE_MAX,
  email: DOCTOR_EMAIL_MAX,
  website: DOCTOR_WEBSITE_MAX,
};

/**
 * What a list of doctors or of patients may be ordered by, as the `sort` of
 * `GET /doctors` and `GET /patients`: when the record was made, its name, how
 * many cases it has, and when its newest case was taken in. Each reads upward
 * as it stands and downward with a leading minus.
 */
export const CLIENT_SORT_FIELDS = ['createdAt', 'name', 'cases', 'lastCase'] as const;
export type ClientSortField = (typeof CLIENT_SORT_FIELDS)[number];
export type ClientSort = ClientSortField | `-${ClientSortField}`;

/**
 * The four orders the two screens offer: newest record first, which is also
 * what the server answers when none is asked for, by name, the most cases
 * first, and the latest case first.
 */
export const CLIENT_SORTS: readonly ClientSort[] = ['-createdAt', 'name', '-cases', '-lastCase'];
export const DEFAULT_CLIENT_SORT: ClientSort = '-createdAt';

/**
 * The order a list is read in, from what was asked: one of the four words with
 * its direction, or newest record first for nothing and for a word that is not
 * one of them, so a stale bookmark still lists.
 */
export function clientSortOf(sort: string | null | undefined): {
  field: ClientSortField;
  descending: boolean;
} {
  const asked = (sort ?? '').trim();
  const descending = asked.startsWith('-');
  const word = descending ? asked.slice(1) : asked;
  const field = CLIENT_SORT_FIELDS.find((each) => each === word);

  return field === undefined ? { field: 'createdAt', descending: true } : { field, descending };
}

/**
 * Where a doctor's and a patient's profile open, by the record's id: under the
 * Doctors and the Patients screens, and held by them. Every link to a profile
 * asks here, so the two routes are named in one place.
 */
export function doctorPath(id: string): string {
  return `/doctors/${encodeURIComponent(id)}`;
}

export function patientPath(id: string): string {
  return `/patients/${encodeURIComponent(id)}`;
}

/** The largest number a record's id may be: the column is a 32 bit integer. */
const CLIENT_ID_MAX = 2_147_483_647;

/**
 * A record's id as it travels: a string of digits, as a catalogue item's is.
 * Read as the server reads one: trimmed, digits and nothing else, above zero.
 * Null for anything else, which names no record, so an address somebody
 * mistyped is a record that was not found rather than a request.
 */
export function parseClientId(raw: string | null | undefined): string | null {
  const text = (raw ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  const id = Number(text);
  return id > 0 && id <= CLIENT_ID_MAX ? String(id) : null;
}

/** The two kinds of record. */
export type ClientKind = 'doctor' | 'patient';

/**
 * What the ID of each kind is made of: the letters it begins with, and how
 * many digits its number is padded to. A number with more digits than that is
 * written in full, so `DR-12345` is doctor 12345.
 */
const CODE: Record<ClientKind, { letters: string; digits: number }> = {
  doctor: { letters: 'DR', digits: 4 },
  patient: { letters: 'PT', digits: 5 },
};
const CLIENT_KINDS: readonly ClientKind[] = ['doctor', 'patient'];

function codeOf(kind: ClientKind, id: string | number | null | undefined): string {
  const number = parseClientId(typeof id === 'number' ? String(id) : id);
  if (number === null) return '';

  const { letters, digits } = CODE[kind];
  return `${letters}-${number.padStart(digits, '0')}`;
}

/**
 * A doctor's ID, from the id of the record: `DR-` and the number padded to
 * four digits, so doctor 12 is `DR-0012`. The request of 6 October 2026
 * (third), item 2. Made from the record's own number, so it is never typed and
 * never used twice. Empty for anything that is not the id of a record, a case
 * nobody linked to a doctor among them.
 */
export function doctorCode(id: string | number | null | undefined): string {
  return codeOf('doctor', id);
}

/** A patient's ID, likewise: `PT-` and the number padded to five, so patient 31 is `PT-00031`. */
export function patientCode(id: string | number | null | undefined): string {
  return codeOf('patient', id);
}

/**
 * An ID as somebody types one, read back to the record it names: `DR-12`,
 * `dr12`, `DR 0012`, `PT-00031` and `pt 31` all read. As the server reads one
 * (its ClientRules.ParseCode): spaces round the whole of it ignored; the two
 * letters in either case; then any spaces and hyphens; then digits and nothing
 * else, with or without the zeros the ID is padded by. For a doctor that is
 * `/^dr[ -]*(\d+)$/i` on the trimmed text. Null for anything else, a bare
 * number among them: a number alone says nothing of which kind of record it
 * is.
 */
export function parseClientCode(
  text: string | null | undefined,
): { kind: ClientKind; id: string } | null {
  const typed = (text ?? '').trim();

  for (const kind of CLIENT_KINDS) {
    const { letters } = CODE[kind];
    if (typed.slice(0, letters.length).toUpperCase() !== letters) continue;

    const digits = typed.slice(letters.length).replace(/^[ -]+/, '');
    const id = /^\d+$/.test(digits) ? parseClientId(digits) : null;
    return id === null ? null : { kind, id };
  }
  return null;
}

/** An ID of the kind asked for, or a bare number, which is read as the id itself. */
function idTyped(kind: ClientKind, text: string | null | undefined): string | null {
  const code = parseClientCode(text);
  if (code !== null) return code.kind === kind ? code.id : null;
  return parseClientId(text);
}

/**
 * The doctor a search box means when what was typed is an ID: `DR-12`, `dr 12`
 * or the number alone. Null for a patient's ID and for anything that is not
 * one. The two lists search by it: a `q` that reads here also matches the
 * record of exactly that id, beside whatever the words match. Exactly: `DR-001`
 * is doctor 1, and does not find `DR-0012`.
 */
export function parseDoctorCode(text: string | null | undefined): string | null {
  return idTyped('doctor', text);
}

/** The same for a patient: `PT-00031`, `pt 31` or the number alone. */
export function parsePatientCode(text: string | null | undefined): string | null {
  return idTyped('patient', text);
}

/** The refusals, worded once for the mock server, the screens and the tests. The API sends the same. */
export const CLIENT_REFUSAL = {
  doctorNameRequired: "Enter the doctor's name",
  patientNameRequired: "Enter the patient's name",
  emailInvalid: 'Enter a valid email address',
  /**
   * A telephone number that is not one: too long, without the `+` and the
   * country's code it has to begin with, or not a number of that country. The
   * request of 6 October 2026 (fifth), section 2.
   */
  phoneInvalid: 'Enter a valid phone number with its country code',
  /** A 409, not a field's 400: another doctor has the name and the clinic. */
  doctorExists: 'A doctor with this name and clinic is already in the system.',
  /**
   * A 409 as well: some patient already has the name, under whichever doctor.
   * Not a wall: the same request sent again with `allowSameName` makes the
   * second record, for a different person of the same name.
   */
  patientExists: 'A patient with this name is already in the system.',
  /** A doctor's id, on a case, that names no record. */
  chooseDoctor: 'Choose a doctor from the list.',
  /** A patient's id, on a case, that names no record. */
  choosePatient: 'Choose a patient from the list.',
  /** A record with any job order, a deleted one included, stays. */
  doctorHasCases: 'This doctor has cases, so the record cannot be deleted.',
  patientHasCases: 'This patient has cases, so the record cannot be deleted.',
  /** An edit made on a copy somebody has changed since: the 409 of a stale If-Match. */
  doctorChanged: 'Somebody else changed this doctor while you were editing. Refresh and try again.',
  patientChanged: 'Somebody else changed this patient while you were editing. Refresh and try again.',
  /** An edit whose body is not an object of fields at all. */
  nothingSent: 'Send the fields to change.',
  /** Reading the records, their cases and the intake lookups without seeIdentity. */
  seeNotAllowed: 'You do not have permission to see doctors and patients.',
  /** Adding or deleting a record, or editing a doctor's details, without manageClients. */
  manageNotAllowed: 'You cannot change doctors and patients.',
  /** Changing a doctor's or a patient's name without renameClients. */
  renameNotAllowed: 'You cannot rename doctors and patients.',
  doctorNotFound: 'Doctor was not found.',
  patientNotFound: 'Patient was not found.',
  /** A `show` that is not one of the list's own, on either list. A 400 on `show`. */
  notAFilter: 'That is not a filter of this list',
  /** Reading or running the conversion of the old addresses and numbers without `admin`. */
  convertNotAllowed: 'Only management may convert addresses and phone numbers.',
  /**
   * A 409 from running the conversion: somebody saved a doctor between its
   * read and its write, so it kept nothing. Running it again is the answer.
   */
  conversionInterrupted:
    'A doctor was changed while the conversion ran, so nothing was converted. Try again.',
} as const;

/**
 * What the server answers a CASE with when it names its doctor or its patient
 * neither by a record nor by a name. Its intake sentences, full stop included,
 * which are older than the records and kept as they were, so a build already
 * installed reads the words it expects. Not the two sentences of a record
 * above, which have none.
 */
export const CASE_NAME_REFUSAL = {
  doctor: "Enter the doctor's name.",
  patient: "Enter the patient's name.",
} as const;

/**
 * The refusals of merging one doctor into another. The request of
 * 6 October 2026 (third), item 1.
 */
export const MERGE_REFUSAL = {
  /** Merging without mergeDoctors. */
  notAllowed: 'You cannot merge doctors.',
  /** No doctor to merge was named, or the one named is no record. A 400 on `duplicateId`. */
  chooseDuplicate: 'Choose the doctor to merge.',
  /** The doctor to merge is the doctor being kept. A 400 on `duplicateId`. */
  sameDoctor: 'A doctor cannot be merged with itself.',
} as const;

/**
 * What is wrong with a merge before it is sent, or null when nothing is: the
 * doctor to merge is named, and is not the doctor kept. Whether it is a record
 * at all is the server's to say, in the first of the two sentences.
 */
export function mergeProblem(
  keepId: string | null | undefined,
  duplicateId: string | null | undefined,
): string | null {
  const duplicate = parseClientId(duplicateId);
  if (duplicate === null) return MERGE_REFUSAL.chooseDuplicate;
  return duplicate === parseClientId(keepId) ? MERGE_REFUSAL.sameDoctor : null;
}

/** The 400 for a field typed past its limit, naming the limit. */
export function tooLongDetail(max: number): string {
  return `Keep it to ${String(max)} characters`;
}

/**
 * A name as a record keeps it: trimmed, with every run of white space inside
 * it as one space. Its capitals are left as they were typed. Empty for a name
 * that is missing or blank. A doctor's clinic is kept the same way.
 */
export function cleanName(name: string | null | undefined): string {
  return (name ?? '').trim().replace(/\s+/g, ' ');
}

/**
 * What two names are compared by: the cleaned name in lower case. `Dr.  jane
 * ROE ` and `Dr. Jane Roe` are one doctor. Nothing cleverer: `Dr. Jane Roe`
 * and `Dr Jane Roe` stay two names, because guessing that they are one person
 * is how two people become one record.
 */
export function normaliseName(name: string | null | undefined): string {
  return cleanName(name).toLowerCase();
}

/** Whether two names are the same name. Two blank names are not: neither is a name. */
export function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  const key = normaliseName(a);
  return key !== '' && key === normaliseName(b);
}

/**
 * Whether two doctors would be the same record: the same name and the same
 * clinic, each compared as names are. No clinic is a clinic of its own, so two
 * doctors of one name with none between them are the same, and one with a
 * clinic and one without are not.
 */
export function sameDoctor(
  name: string | null | undefined,
  clinic: string | null | undefined,
  otherName: string | null | undefined,
  otherClinic: string | null | undefined,
): boolean {
  return sameName(name, otherName) && normaliseName(clinic) === normaliseName(otherClinic);
}

/**
 * The titles a doctor's name may begin with, which say nothing of who the
 * doctor is: dr, doctor, prof, professor, and the three Arabic ways of writing
 * doctor (the letter dal alone, duktur, and al-duktur). Written by code point
 * so the line reads the same in every editor.
 */
const TITLES: readonly string[] = [
  'dr',
  'doctor',
  'prof',
  'professor',
  '\u062f',
  '\u062f\u0643\u062a\u0648\u0631',
  '\u0627\u0644\u062f\u0643\u062a\u0648\u0631',
];

/** The title a name begins with, when a space, a full stop or the end of the name follows it. */
function titleAt(text: string): string | undefined {
  return TITLES.find((title) => {
    if (!text.startsWith(title)) return false;
    const next = text.charAt(title.length);
    return next === '' || next === ' ' || next === '.';
  });
}

/**
 * The loose key two doctors are compared by to find likely duplicates. The
 * request of 6 October 2026 (third), item 1. In three steps, which the server
 * takes in the same order (its ClientNames.LooseKey):
 *
 *   1. the name as the name rule compares it: trimmed, one space between
 *      words, lower case;
 *   2. while it begins with one of the seven titles and that title is followed
 *      by a space, a full stop or the end of the name, the title goes, and
 *      with it every space and full stop that follows it. Repeated, so
 *      `Prof. Dr. Ahmed` loses both. Nothing else is skipped: `Drake` is no
 *      title, and `Dr-Ahmed` keeps its `dr`;
 *   3. every character that is not a letter or a decimal digit goes.
 *
 * So `Dr. Ahmed Ali`, `Dr Ahmed Ali`, `AHMED  ALI` and `Doctor Ahmed-Ali` all
 * come to `ahmedali`.
 *
 * Looser than the name rule on purpose, and used for nothing but a suggestion:
 * `sameName` still decides which record a typed name means, and two doctors
 * with one key stay two records until somebody merges them. Empty for a name
 * that is nothing but titles and punctuation.
 */
export function duplicateKey(name: string | null | undefined): string {
  let text = normaliseName(name);

  for (let title = titleAt(text); title !== undefined; title = titleAt(text)) {
    text = text.slice(title.length).replace(/^[ .]+/, '');
  }
  return text.replace(/[^\p{L}\p{Nd}]/gu, '');
}

/**
 * Whether two doctors' names are a possible duplicate: the same loose key. An
 * empty key matches nothing, another empty key included: a name that is all
 * title is nobody in particular.
 */
export function possibleDuplicate(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const key = duplicateKey(a);
  return key !== '' && key === duplicateKey(b);
}

/** By character code, as the server orders the possible duplicates, so both put them alike. */
function byCode(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** A doctor as the search for possible duplicates reads one: the id and the name, and nothing else. */
export interface NamedDoctor {
  id: string;
  name: string;
}

/**
 * The doctors whose loose key another doctor shares, by id, in the order the
 * Possible duplicates list shows them: each group together, the groups by
 * their key, and inside a group by name and then by id. A doctor whose key is
 * empty is in no group. The server's DoctorDuplicates.InGroups, with every
 * comparison of text by character code as there.
 */
export function possibleDuplicateIds(doctors: readonly NamedDoctor[]): string[] {
  const groups = new Map<string, NamedDoctor[]>();
  for (const doctor of doctors) {
    const key = duplicateKey(doctor.name);
    if (key === '') continue;
    groups.set(key, [...(groups.get(key) ?? []), doctor]);
  }

  return [...groups]
    .filter(([, group]) => group.length > 1)
    .sort(([a], [b]) => byCode(a, b))
    .flatMap(([, group]) =>
      [...group].sort((a, b) => byCode(a.name, b.name) || Number(a.id) - Number(b.id)),
    )
    .map((doctor) => doctor.id);
}

/**
 * The other doctors that look like this one, by id, in order of name and then
 * of id: what a doctor's profile lists as its possible duplicates. Empty when
 * the doctor is not among those handed over, and when its key is empty.
 */
export function similarDoctorIds(id: string, doctors: readonly NamedDoctor[]): string[] {
  const doctor = doctors.find((each) => each.id === id);
  if (doctor === undefined) return [];

  return doctors
    .filter((other) => other.id !== id && possibleDuplicate(other.name, doctor.name))
    .sort((a, b) => byCode(a.name, b.name) || Number(a.id) - Number(b.id))
    .map((other) => other.id);
}

/** How many of a patient's doctors a list names before it says how many more there are. */
export const PATIENT_DOCTORS_SHOWN = 2;

/**
 * A patient's doctors as a row of a list has room for them: the first few, in
 * the order the record carries them, and how many are left unnamed. A list
 * writes the names and then `+N more`.
 */
export function doctorsInBrief(
  doctors: readonly PatientDoctor[],
  shown: number = PATIENT_DOCTORS_SHOWN,
): { shown: PatientDoctor[]; more: number } {
  const first = doctors.slice(0, shown);
  return { shown: first, more: doctors.length - first.length };
}

/** Whether a patient has a case with this doctor, by the doctor's id. */
export function patientSeenBy(
  patient: Pick<Patient, 'doctors'>,
  doctorId: string | null | undefined,
): boolean {
  return doctorId != null && patient.doctors.some((doctor) => doctor.id === doctorId);
}

/** A typed detail as a record keeps it: trimmed, and null rather than empty. */
export function blankToNull(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
}

/**
 * An email address, loosely: one `@` with something on each side of it. Enough
 * to catch a telephone number typed in the wrong box, and no more, because the
 * lab writes to these addresses itself and a stricter rule would only refuse
 * real ones.
 */
export function validEmail(value: string): boolean {
  const text = value.trim();
  const at = text.indexOf('@');
  return at > 0 && at < text.length - 1 && !text.includes('@', at + 1);
}

/**
 * What is sent for a doctor. Everything but the name may be left empty, until
 * there is an address: an address is all or nothing, and one that is sent
 * needs its clinic too.
 */
export interface DoctorInput {
  name: string;
  clinic?: string | null;
  /**
   * The telephone number with its country code, `+971501234567`; spaces,
   * hyphens, dots and round brackets in it are ignored. Null or blank clears
   * it.
   */
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  /** The whole address, which replaces the one the record has. Null clears it. */
  address?: AddressInput | null;
}

/** What somebody types for a patient: the name, which is all of a patient's record. */
export interface PatientInput {
  name: string;
  /**
   * True to keep the name although another patient already has it: a different
   * person of the same name. Sent only once the server has said the name
   * exists and the person has chosen to add or rename all the same.
   */
  allowSameName?: boolean;
}

/** Something wrong with what was typed: the field it is on, and the server's sentence. */
export interface ClientProblem {
  field: DoctorField;
  message: string;
}

/** The five lines of text of a doctor's record as they are kept. */
export interface DoctorRecord {
  name: string;
  clinic: string | null;
  /** As it was sent, trimmed. The server keeps the E.164 it reads from this. */
  phone: string | null;
  email: string | null;
  website: string | null;
}

/**
 * A doctor's five lines of text as the record keeps them. The name and the
 * clinic are cleaned as names are, since the two together are what tells one
 * doctor from another; the rest are trimmed, and a blank detail is no detail.
 * The address is tidied by its own rule, `tidyAddress`.
 */
export function doctorRecord(input: DoctorInput): DoctorRecord {
  return {
    name: cleanName(input.name),
    clinic: blankToNull(cleanName(input.clinic)),
    phone: blankToNull(input.phone),
    email: blankToNull(input.email),
    website: blankToNull(input.website),
  };
}

/**
 * A telephone number as the server reads one before it asks whether it is a
 * number: without the white space of any kind (a tab and a no-break space
 * too), the hyphens, the dots and the round brackets people write one with.
 * `+971 (50) 123-4567` reads `+971501234567`.
 */
export function phoneCandidate(text: string | null | undefined): string {
  return (text ?? '').replace(/[\s\-.()]/g, '');
}

/** A number the telephone library could read: as E.164, and as people read one. */
export interface ReadPhone {
  e164: string;
  display: string;
}

/**
 * Whether a candidate is a real number, and how it is written: the telephone
 * library's answer, with no country assumed, or null when it is not valid.
 * The library is not the domain's to load, so whoever holds it hands this in.
 */
export type PhoneReader = (candidate: string) => ReadPhone | null;

/**
 * Whether a candidate is written as a number at all: a plus, and then digits
 * and nothing else. Stricter than the telephone library, which reads past an
 * extension or letters (`+971 50 123 4567 ext 5`, `+1 800 FLOWERS`) and would
 * drop them on the way to E.164: the server refuses those instead, and so
 * does the conversion of the old text.
 */
export function phoneWritten(candidate: string): boolean {
  return /^\+[0-9]+$/.test(candidate);
}

/**
 * What is wrong with a telephone number as sent, or null when it will do. A
 * blank one is no number, and no fault. Otherwise it is no longer than forty
 * characters, measured trimmed and before anything is taken out of it; once
 * its punctuation is gone it is a plus and digits and nothing else; and it is
 * a valid number of the country its code names. One sentence for all three.
 * Without a reader the last is left to the server.
 */
export function phoneProblem(
  text: string | null | undefined,
  read?: PhoneReader,
): string | null {
  const typed = (text ?? '').trim();
  if (typed === '') return null;

  const candidate = phoneCandidate(typed);
  const fits = typed.length <= DOCTOR_PHONE_MAX && phoneWritten(candidate);
  return fits && read?.(candidate) !== null ? null : CLIENT_REFUSAL.phoneInvalid;
}

/**
 * The digits a search for a telephone number is made with, or null when what
 * was typed is not one: at least three digits, and nothing but digits, spaces
 * and `+ - ( )`. The zeros a number is dialled with inside its own country go,
 * since the E.164 searched has none: `050 123` finds `+97150123...`.
 */
export function phoneSearchDigits(q: string | null | undefined): string | null {
  const typed = (q ?? '').trim();
  if (!/^[\d +\-()]+$/.test(typed)) return null;

  const digits = typed.replace(/\D/g, '');
  if (digits.length < 3) return null;

  // Nothing but zeros is no number to look for.
  const dialled = digits.replace(/^0+/, '');
  return dialled === '' ? null : dialled;
}

/**
 * On `clinic`, or null: an address is delivered to a clinic or a company, so
 * a record that has an address has a clinic. Asked only of a request that
 * sent the address or the clinic, so an older record is not refused for it
 * while something else is being changed. The sentence is
 * `ADDRESS_REFUSAL.clinicRequired`.
 */
export function clinicMissing(clinic: string | null | undefined, hasAddress: boolean): boolean {
  return hasAddress && cleanName(clinic) === '';
}

/**
 * Everything wrong with a doctor as typed, one message for each field that has
 * something wrong, in the order the form shows them. Empty when the record
 * will do. This is the server's own list, so a form can mark every field that
 * needs attention at once and in the server's words.
 *
 * The name is required; each field keeps to its limit, measured as the record
 * will keep it; a telephone number, when one is given, is one, by
 * `phoneProblem`; and an email address, when one is given, is one, which is
 * asked before how long it is: a long line with no `@` in it is not an
 * address. Whether another doctor already has the name and the clinic is the
 * server's to say, with a 409: it holds the list.
 *
 * The address is not asked about here: its parts are checked by
 * `addressProblems`, after these five, and the clinic it needs by
 * `clinicMissing`, last of all.
 */
export function doctorProblems(input: DoctorInput, readPhone?: PhoneReader): ClientProblem[] {
  const record = doctorRecord(input);
  const problems: ClientProblem[] = [];

  if (record.name === '') {
    problems.push({ field: 'name', message: CLIENT_REFUSAL.doctorNameRequired });
  }

  for (const field of DOCTOR_FIELDS) {
    const value = record[field];
    // A name that is missing has been said; a detail that is missing is no fault.
    if (value === null || value === '') continue;

    if (field === 'phone') {
      const badPhone = phoneProblem(value, readPhone);
      if (badPhone !== null) problems.push({ field, message: badPhone });
    } else if (field === 'email' && !validEmail(value)) {
      problems.push({ field, message: CLIENT_REFUSAL.emailInvalid });
    } else if (value.length > DOCTOR_FIELD_MAX[field]) {
      problems.push({ field, message: tooLongDetail(DOCTOR_FIELD_MAX[field]) });
    }
  }
  return problems;
}

/** The first thing wrong with a doctor as typed, which is the 400's `detail`, or null when nothing is. */
export function checkDoctor(input: DoctorInput, readPhone?: PhoneReader): ClientProblem | null {
  return doctorProblems(input, readPhone)[0] ?? null;
}

/**
 * What is wrong with a name typed for a record or on a case, or null when it
 * will do: missing, in the words given for that, or longer than 160 characters
 * once cleaned. A doctor's name and a patient's are held to the same.
 */
export function nameProblem(name: string | null | undefined, required: string): string | null {
  const cleaned = cleanName(name);
  if (cleaned === '') return required;
  return cleaned.length > NAME_MAX ? tooLongDetail(NAME_MAX) : null;
}

/**
 * What is wrong with a patient as typed, or null when nothing is: the name is
 * required and keeps to its limit, for a new record and for a rename alike.
 * Whether another patient already has the name is the server's to say, with a
 * 409 the person may answer by asking for a second record of the name.
 */
export function checkPatient(input: PatientInput): ClientProblem | null {
  const badName = nameProblem(input.name, CLIENT_REFUSAL.patientNameRequired);
  return badName === null ? null : { field: 'name', message: badName };
}
